// [목적] 게스트 인터랙션 가드 훅 — 인증 여부에 따라 액션을 즉시 실행하거나 로그인 유도 모달을 노출한다.
// [이유] SLE-FE-04 (auth-signup-login-expansion Wave C) — chat.create/folio.like/folio.bookmark/folio.comment
//         진입점에서 비로그인 사용자의 인터랙션을 가로채 LoginPromptModal 을 열어 로그인 유도.
//         useAuth 의 isAuthenticated 를 단일 진실 소스로 사용.
// [사이드 이팩트] isAuthenticated === false 시 guard(action) 호출 → action 미실행 + isPromptOpen=true.
//                 isAuthenticated === true 시 guard(action) 호출 → action 즉시 실행 + 모달 미노출.
//                 intent replay(로그인 후 action 자동 재실행)는 v1.0 외 — 미구현.
// [수정 시 고려사항] intent replay 가 필요해지는 시점에 pendingAction ref 패턴을 추가하여 로그인 복귀 후 실행.
//                   isAuthenticated 는 status==='authenticated' 로 판단 — loading 중에는 gate 불통과(안전 기본값).

import { useCallback, useState } from 'react';
import { useAuthContext } from '../providers/AuthProvider.js';

// ─── 타입 ──────────────────────────────────────────────────────────────────

/**
 * [목적] 게스트 인터랙션 의도 구분 — intent 별 LoginPromptModal 카피 결정에 사용
 * [이유] SLE-FE-04 AD-4: 진입점별 의도를 구체화하여 사용자에게 맥락 있는 안내 제공
 * [사이드 이팩트] 없음 (타입 선언만)
 * [수정 시 고려사항] 신규 인터랙션 진입점 추가 시 이 union 을 확장하고 LoginPromptModal 카피 맵도 갱신
 */
// [수정] FlowLine A7(신고): 'report.create' 추가 — 폴리오/댓글 신고 버튼 게스트 가드 진입점.
//        useAuthGate.ts 헤더 주석의 "신규 인터랙션 진입점 추가 시 이 union 을 확장" 관례를 그대로 따름.
// [수정] QA P2-01: 'collection.create' 추가 — Sidebar "새 컬렉션" 게스트 가드 진입점. LoginPromptModal
//        INTENT_COPY 와 동기.
// [수정] festival-comments(2026-09-21): 'festival.comment' 추가 — 축제 상세 댓글 작성 게스트 가드 진입점.
//        LoginPromptModal INTENT_COPY 와 동기(카피는 folio.comment 와 동일).
export type AuthIntent =
  | 'chat.create'
  | 'folio.like'
  | 'folio.bookmark'
  | 'folio.comment'
  | 'festival.comment'
  | 'report.create'
  | 'collection.create';

export interface UseAuthGateResult {
  /**
   * 액션을 guard 로 감쌈.
   * - isAuthenticated=true → action 즉시 실행
   * - isAuthenticated=false → action 미실행, isPromptOpen=true + pendingIntent=intent 설정
   */
  guard: (action: () => void | Promise<void>) => void;
  /** 로그인 유도 모달 노출 여부 */
  isPromptOpen: boolean;
  /** 모달 닫기 — isPromptOpen=false + pendingIntent=null */
  closePrompt: () => void;
  /** 현재 게스트가 시도한 인터랙션 의도 (모달이 닫히면 null) */
  pendingIntent: AuthIntent | null;
}

// ─── 훅 ────────────────────────────────────────────────────────────────────

/**
 * [목적] 인증 게이트 훅 — isAuthenticated 기준으로 액션 실행 or 로그인 모달 노출
 * [이유] SLE-FE-04: 비로그인 사용자의 보호 액션 진입점마다 별도 인스턴스로 사용
 * [사이드 이팩트] 상태(isPromptOpen/pendingIntent)는 훅 인스턴스 로컬 — 전역 공유 없음.
 * [수정 시 고려사항] intent 는 훅 생성 시 고정 — 진입점별 인스턴스를 권장(AD-4 결정).
 *                   loading 상태(status==='loading')는 isAuthenticated=false 로 처리 — 안전 기본값.
 */
export function useAuthGate(intent: AuthIntent): UseAuthGateResult {
  const { status } = useAuthContext();
  const isAuthenticated = status === 'authenticated';

  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [pendingIntent, setPendingIntent] = useState<AuthIntent | null>(null);

  /**
   * [목적] 인증 게이트 래퍼 — 인증 여부에 따라 action 실행 또는 모달 노출
   * [이유] 진입점마다 guard(action) 으로 감싸면 한 곳에서 isAuthenticated 분기를 처리
   * [사이드 이팩트] 비인증 시 action 미실행 — 사용자는 로그인 후 직접 재시도해야 함(intent replay v2.0+)
   * [수정 시 고려사항] async action 의 reject 는 guard 내부에서 throw 아닌 호출자에게 bubble up.
   *                   guard 자체는 Promise<void> 를 반환하지 않음 — void 처리.
   */
  const guard = useCallback(
    (action: () => void | Promise<void>): void => {
      if (isAuthenticated) {
        // 인증 사용자 — action 즉시 실행 (비동기 에러는 호출자 책임)
        void Promise.resolve(action());
      } else {
        // 비인증 — action 미실행, 모달 노출
        setPendingIntent(intent);
        setIsPromptOpen(true);
      }
    },
    [isAuthenticated, intent],
  );

  /**
   * [목적] 모달 닫기 — isPromptOpen=false + pendingIntent=null
   * [이유] 사용자가 모달을 닫거나 취소할 때 상태를 초기화
   * [사이드 이팩트] 없음
   * [수정 시 고려사항] intent replay 구현 시 여기서 pendingAction ref 도 null 처리 필요
   */
  const closePrompt = useCallback((): void => {
    setIsPromptOpen(false);
    setPendingIntent(null);
  }, []);

  return {
    guard,
    isPromptOpen,
    closePrompt,
    pendingIntent,
  };
}
